import { Component, ElementRef, OnInit, ViewChild, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatMenuModule } from '@angular/material/menu';
import { MatSelectModule } from '@angular/material/select';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatDialog } from '@angular/material/dialog';
import { Observable } from 'rxjs';
import { HotToastService } from '@ngxpert/hot-toast';
import { IssueService } from '../../../services/issue.service';
import { VersionService } from '../../../services/version.service';
import { ConfirmationDialogService } from '../../../services/confirmation-dialog.service';
import { CurrentUserState } from '../../../state/current-user.state';
import { IdenticonComponent } from '../../shared/identicon/identicon.component';
import { Issue, IssueAttachment, IssueComment } from '../../../models/issue';
import { IssueStatusChangeRequest } from '../../../models/api/issueRequests';
import {
  DeveloperNotesDialogComponent,
  DeveloperNotesDialogData
} from '../developer-notes-dialog/developer-notes-dialog.component';
import { ISSUE_STATUS_LABELS } from '../../../models/enums/issueStatus.enum';
import { IssueType } from '../../../models/enums/issueType.enum';
import { IssuePriority } from '../../../models/enums/issuePriority.enum';

@Component({
  selector: 'app-issue-detail',
  imports: [
    CommonModule, FormsModule,
    MatButtonModule, MatIconModule, MatFormFieldModule,
    MatInputModule, MatMenuModule, MatSelectModule, MatTooltipModule,
    IdenticonComponent
  ],
  template: `
    <div class="component-container">
      @if (loading) {
        <p>Loading…</p>
      } @else if (issue) {
        <!-- Page header (matches edit pages across the app) -->
        <div class="component-header">
          <div class="component-header-title">
            <h2>Issue #{{ issue.number }}</h2>
            <p class="component-header-subtitle">
              {{ issue.categoryName }}@if (issue.subCategoryName) { <span> / {{ issue.subCategoryName }}</span> }
              · Reported by {{ issue.reportedByUserName }}
            </p>
          </div>
          <div class="header-actions">
            <span class="chip type">{{ issue.type }}</span>
            <span class="chip priority" [ngStyle]="priorityStyle(issue.priority)">
              <mat-icon>flag</mat-icon>{{ issue.priority }}
            </span>
            @if (canEdit && !editing) {
              <button mat-icon-button matTooltip="Edit" (click)="startEdit()"><mat-icon>edit</mat-icon></button>
            }
            @if (canReporterAct || canDelete) {
              <button mat-icon-button matTooltip="More actions" [matMenuTriggerFor]="actionsMenu"><mat-icon>more_vert</mat-icon></button>
              <mat-menu #actionsMenu="matMenu">
                @if (canReporterAct) {
                  <button mat-menu-item (click)="withdraw()"><mat-icon>undo</mat-icon><span>Withdraw</span></button>
                  <button mat-menu-item (click)="deleteOwn()"><mat-icon>delete</mat-icon><span>Delete</span></button>
                } @else if (canDelete) {
                  <button mat-menu-item (click)="deleteIssue()"><mat-icon>delete</mat-icon><span>Delete</span></button>
                }
              </mat-menu>
            }
            <button mat-stroked-button (click)="back()"><mat-icon>arrow_back</mat-icon> Back</button>
          </div>
        </div>

        <div class="grid">
          <div class="main">
            <!-- Title + description in one card -->
            <section class="section-container nrg-border-radius">
              @if (!editing) {
                <div class="section-header issue-title">{{ issue.title }}</div>
                <div class="section-content">
                  <p class="description">{{ issue.description }}</p>
                </div>
              } @else {
                <div class="section-header">Edit issue</div>
                <div class="section-content">
                  <mat-form-field appearance="fill" class="full">
                    <mat-label>Title</mat-label>
                    <input matInput [(ngModel)]="editTitle" maxlength="200" />
                  </mat-form-field>
                  <div class="edit-row">
                    <mat-form-field appearance="fill">
                      <mat-label>Type</mat-label>
                      <mat-select [(ngModel)]="editType">
                        @for (t of types; track t) { <mat-option [value]="t">{{ t }}</mat-option> }
                      </mat-select>
                    </mat-form-field>
                    <mat-form-field appearance="fill">
                      <mat-label>Priority</mat-label>
                      <mat-select [(ngModel)]="editPriority">
                        @for (p of priorities; track p) { <mat-option [value]="p">{{ p }}</mat-option> }
                      </mat-select>
                    </mat-form-field>
                  </div>
                  <mat-form-field appearance="fill" class="full">
                    <mat-label>Description</mat-label>
                    <textarea matInput [(ngModel)]="editDescription" rows="6"></textarea>
                  </mat-form-field>
                  <div class="right">
                    <button mat-button (click)="cancelEdit()">Cancel</button>
                    <button mat-flat-button color="primary"
                            [disabled]="!editTitle.trim() || !editDescription.trim()"
                            (click)="saveEdit()">Save</button>
                  </div>
                </div>
              }
            </section>

            <!-- Attachments -->
            <section class="section-container nrg-border-radius">
              <div class="section-header">Attachments</div>
              <div class="section-content">
                @if (issue.attachments.length === 0) { <p class="muted">No attachments.</p> }
                @for (a of issue.attachments; track a.id) {
                  <div class="attachment">
                    <mat-icon>insert_drive_file</mat-icon>
                    <button mat-button (click)="download(a)">{{ a.fileName }}</button>
                    <span class="muted">{{ (a.fileSize / 1024) | number:'1.0-0' }} KB</span>
                  </div>
                }
                @if (canCreate) {
                  <div class="upload">
                    <button type="button" mat-stroked-button (click)="fileInput.click()">
                      <mat-icon>attach_file</mat-icon> Choose file
                    </button>
                    <input #fileInput type="file" hidden (change)="onFileSelected($event)" />
                    @if (selectedFile) {
                      <span class="muted">{{ selectedFile.name }}</span>
                      <button mat-flat-button color="primary" (click)="uploadAttachment()">Upload</button>
                    }
                  </div>
                }
              </div>
            </section>

            <!-- Comments (Jira-style) -->
            <section class="section-container nrg-border-radius">
              <div class="section-header">
                <span>Comments</span>
                @if (isDeveloper) {
                  <button mat-stroked-button class="dev-notes-btn"
                          [class.has-notes]="issue.hasDeveloperNotes"
                          [matTooltip]="issue.hasDeveloperNotes
                            ? 'This issue has developer notes — only the dev team can see them'
                            : 'Add internal notes for the dev team'"
                          (click)="openDeveloperNotes()">
                    {{ issue.hasDeveloperNotes ? 'Developer notes' : 'Add developer notes' }}
                  </button>
                }
              </div>
              <div class="section-content">
                @if (issue.comments.length === 0) { <p class="muted">No comments yet.</p> }
                @for (c of issue.comments; track c.id) {
                  <div class="jira-comment">
                    <app-identicon class="avatar" [name]="c.authorUserName" [size]="36" />
                    <div class="comment-main">
                      <div class="comment-head">
                        <span class="author">{{ c.authorUserName }}</span>
                        <span class="time">{{ c.createdDate | date:"MMMM d, y 'at' h:mm a" }}</span>
                        @if (c.editedDate) {
                          <span class="time" [matTooltip]="'Edited ' + (c.editedDate | date:'dd/MM/yyyy HH:mm')">· edited</span>
                        }
                        @if (isMyComment(c) && editingCommentId !== c.id) {
                          <button mat-icon-button class="comment-actions" matTooltip="Comment actions"
                                  [matMenuTriggerFor]="commentMenu">
                            <mat-icon>more_horiz</mat-icon>
                          </button>
                          <mat-menu #commentMenu="matMenu">
                            <button mat-menu-item (click)="startCommentEdit(c)">
                              <mat-icon>edit</mat-icon><span>Edit</span>
                            </button>
                            <button mat-menu-item (click)="deleteComment(c)">
                              <mat-icon>delete</mat-icon><span>Delete</span>
                            </button>
                          </mat-menu>
                        }
                      </div>
                      @if (editingCommentId === c.id) {
                        <mat-form-field appearance="fill" class="full">
                          <mat-label>Edit comment</mat-label>
                          <textarea matInput [(ngModel)]="editCommentBody" rows="3"></textarea>
                        </mat-form-field>
                        <div class="right">
                          <button mat-button (click)="cancelCommentEdit()">Cancel</button>
                          <button mat-flat-button color="primary"
                                  [disabled]="!editCommentBody.trim()"
                                  (click)="saveCommentEdit(c)">Save</button>
                        </div>
                      } @else {
                        <div class="comment-body">{{ c.body }}</div>
                      }
                    </div>
                  </div>
                }
                @if (canCreate) {
                  <div class="add-comment">
                    <mat-form-field appearance="fill" class="full">
                      <mat-label>Add a comment</mat-label>
                      <textarea #commentBox matInput [(ngModel)]="newComment" rows="3"></textarea>
                    </mat-form-field>
                    <div class="right">
                      <button mat-flat-button color="primary" [disabled]="!newComment.trim()" (click)="addComment()">Comment</button>
                    </div>
                  </div>
                }
              </div>
            </section>
          </div>

          <div class="side">
            <!-- Status -->
            <section class="section-container nrg-border-radius">
              <div class="section-header">Status</div>
              <div class="section-content">
                @if (canEdit && issue.allowedStatuses.length > 0) {
                  <button class="status-lozenge editable" [ngStyle]="statusStyle(issue.status)" [matMenuTriggerFor]="statusMenu">
                    {{ statusLabel(issue.status) }} <mat-icon>arrow_drop_down</mat-icon>
                  </button>
                  <mat-menu #statusMenu="matMenu">
                    @for (s of issue.allowedStatuses; track s) {
                      <button mat-menu-item class="status-option" [ngStyle]="statusBorderStyle(s)" (click)="selectStatus(s)">
                        {{ statusLabel(s) }}
                      </button>
                    }
                  </mat-menu>
                } @else {
                  <span class="status-lozenge" [ngStyle]="statusStyle(issue.status)">{{ statusLabel(issue.status) }}</span>
                }
              </div>
            </section>

            <!-- Verification: the tester's confirmation that the fix actually works. Shown only once
                 there is something to try (server-computed isVerifiable) or once someone has tried it. -->
            @if (issue.isVerifiable || issue.verifiedDate) {
              <section class="section-container nrg-border-radius">
                <div class="section-header">Verification</div>
                <div class="section-content">
                  @if (issue.verifiedDate) {
                    <div class="verified-chip">
                      <mat-icon>verified</mat-icon>
                      <div>
                        <strong>Verified {{ verifiedWhere(issue.verifiedEnvironment) }}</strong>
                        <div class="small">
                          {{ issue.verifiedByUserName }} · {{ issue.verifiedDate | date:'dd/MM/yyyy HH:mm' }}
                        </div>
                      </div>
                    </div>
                    @if (issue.status === 'Staged') {
                      <p class="muted small ready">Ready to promote to production.</p>
                    }
                  } @else {
                    <p class="muted small">
                      The fix is {{ issue.status === 'Staged' ? 'on staging' : 'in production' }}.
                      Try it and tell us how it went.
                    </p>
                    <div class="verify-actions">
                      <button mat-flat-button color="primary" (click)="verify()">
                        <mat-icon>check</mat-icon> It works
                      </button>
                      <button mat-stroked-button (click)="sendVerificationFailed()">
                        <mat-icon>close</mat-icon> Doesn't work
                      </button>
                    </div>
                  }
                </div>
              </section>
            }

            <!-- Details -->
            <section class="section-container nrg-border-radius">
              <div class="section-header">Details</div>
              <div class="section-content">
                <dl class="meta">
                  <dt>Category</dt>
                  <dd>{{ issue.categoryName }}@if (issue.subCategoryName) { <span> / {{ issue.subCategoryName }}</span> }</dd>
                  <dt>Reporter</dt><dd>{{ issue.reportedByUserName }}</dd>
                  <dt>Opened</dt><dd>{{ issue.createdDate | date:'dd/MM/yyyy HH:mm' }}</dd>
                  @if (issue.reportedInVersion) { <dt>Reported in</dt><dd>{{ issue.reportedInVersion }}</dd> }
                  @if (issue.fixedInVersion) { <dt>Fixed in</dt><dd>{{ issue.fixedInVersion }}</dd> }
                </dl>
              </div>
            </section>

            <!-- Status history -->
            <section class="section-container nrg-border-radius">
              <div class="section-header">History</div>
              <div class="section-content">
                @for (h of issue.stateChanges; track h.id) {
                  <div class="history">
                    <div>
                      @if (h.fromStatus) { <span class="muted">{{ statusLabel(h.fromStatus) }} → </span> }
                      <strong>{{ statusLabel(h.toStatus) }}</strong>
                    </div>
                    <div class="muted small">
                      {{ h.changedByUserName }} · {{ h.changedAt | date:'dd/MM/yyyy HH:mm' }}
                    </div>
                    @if (h.note) { <div class="small">{{ h.note }}</div> }
                  </div>
                }
              </div>
            </section>
          </div>
        </div>
      } @else {
        <p>Issue not found.</p>
      }
    </div>
  `,
  styles: [`
    .header-actions { display: flex; align-items: center; gap: 8px; }
    .chip { padding: 2px 10px; border-radius: 12px; font-size: 0.8em; }
    .chip.type { background: #ede7f6; color: #4527a0; font-weight: 600; }
    .chip.priority {
      font-weight: 700; text-transform: uppercase; font-size: 0.82em;
      padding: 4px 12px; display: inline-flex; align-items: center; gap: 4px;
    }
    .chip.priority mat-icon { font-size: 16px; height: 16px; width: 16px; }
    .grid { display: grid; grid-template-columns: 2fr 1fr; gap: 16px; }

    /* Themed cards (matches director edit / the rest of the app) */
    .section-container {
      background-color: color-mix(in srgb, var(--mat-sys-primary), transparent 95%) !important;
      padding: 1rem;
      margin-bottom: 1rem;
    }
    .section-container .section-header {
      display: flex; justify-content: space-between; align-items: center;
      margin-bottom: 1rem; color: var(--mat-sys-primary);
    }
    .section-container .section-header.issue-title { font-size: 1.1rem; font-weight: 600; }
    .section-content { display: flex; flex-direction: column; }

    .description { white-space: pre-wrap; margin: 0; }
    .muted { color: rgba(0,0,0,0.55); }
    .small { font-size: 0.85em; }
    .full { width: 100%; }
    .edit-row { display: flex; flex-wrap: wrap; gap: 12px; }
    .edit-row mat-form-field { width: 200px; }
    .right { display: flex; justify-content: flex-end; gap: 8px; }
    .attachment { display: flex; align-items: center; gap: 8px; }
    .upload { display: flex; align-items: center; gap: 8px; margin-top: 8px; }

    /* Status */
    .status-lozenge {
      align-self: flex-start;
      display: inline-flex; align-items: center; gap: 4px;
      border: none; border-radius: 4px;
      padding: 7px 12px; font-size: 0.9em; font-weight: 700; text-transform: uppercase; letter-spacing: 0.3px;
    }
    .status-lozenge.editable { cursor: pointer; }
    .status-lozenge.editable:hover { filter: brightness(0.95); }
    .status-lozenge mat-icon { font-size: 20px; height: 20px; width: 20px; }
    /* A little breathing room between transition options */
    .status-option:not(:last-child) { margin-bottom: 3px; }

    /* Developer notes — sits in the Comments header. Quiet outline while empty; once the issue
       carries notes it goes amber, which reads as "something to read here" and stays clear of the
       primary colour the rest of the page is built from. The MDC custom properties are set alongside
       the plain border/colour so the override holds whichever the theme drives the button with. */
    .dev-notes-btn {
      height: 32px; line-height: 30px; padding: 0 14px;
      font-size: 0.8rem; font-weight: 500; letter-spacing: 0.2px;
    }
    .dev-notes-btn.has-notes {
      --mdc-outlined-button-outline-color: #e0a800;
      --mdc-outlined-button-label-text-color: #7a5200;
      background: #fff3cd;
      border-color: #e0a800;
      color: #7a5200;
      font-weight: 600;
    }
    .dev-notes-btn.has-notes:hover {
      background: #ffe8a1;
    }

    /* Jira-style comments */
    .jira-comment { display: flex; gap: 12px; padding: 16px 0; }
    .jira-comment + .jira-comment { border-top: 1px solid var(--mat-sys-outline-variant); }
    .comment-actions { margin-left: auto; }
    .add-comment { margin-top: 24px; }
    .avatar { flex: 0 0 36px; }
    .comment-main { flex: 1; min-width: 0; }
    .comment-head { display: flex; align-items: baseline; gap: 8px; }
    .comment-head .author { font-weight: 600; }
    .comment-head .time { color: rgba(0,0,0,0.55); font-size: 0.85em; }
    .comment-body { white-space: pre-wrap; margin-top: 4px; }

    /* Verification. Green reads as "confirmed" and keeps clear of both the status lozenge palette and
       the amber developer-notes button. */
    .verify-actions { display: flex; gap: 8px; margin-top: 8px; }
    .verified-chip {
      display: flex; align-items: center; gap: 10px;
      background: #e8f5e9; color: #1b5e20;
      border-radius: 4px; padding: 10px 12px;
    }
    .verified-chip mat-icon { color: #2e7d32; }
    .ready { margin: 8px 0 0; }

    .meta { display: grid; grid-template-columns: auto 1fr; gap: 4px 12px; margin: 0; }
    .meta dt { color: rgba(0,0,0,0.55); }
    .meta dd { margin: 0; }
    .history { padding: 6px 0; }
    .history + .history { border-top: 1px solid var(--mat-sys-outline-variant); }
    @media (max-width: 900px) { .grid { grid-template-columns: 1fr; } }
  `]
})
export class IssueDetailComponent implements OnInit {
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private issueService = inject(IssueService);
  private toast = inject(HotToastService);
  private currentUserState = inject(CurrentUserState);
  private versionService = inject(VersionService);
  private confirmationDialog = inject(ConfirmationDialogService);
  private dialog = inject(MatDialog);

  /** The "Add a comment" box — focused after a fix is sent back, so explaining is the next thing you do. */
  @ViewChild('commentBox') private commentBox?: ElementRef<HTMLTextAreaElement>;

  issue?: Issue;
  loading = true;
  statusLabels = ISSUE_STATUS_LABELS;

  editing = false;
  editTitle = '';
  editDescription = '';
  editType = '';
  editPriority = '';
  types = Object.values(IssueType);
  priorities = Object.values(IssuePriority);

  newComment = '';
  /** Id of the comment currently open in the inline editor, if any. */
  editingCommentId: string | null = null;
  editCommentBody = '';
  selectedFile: File | null = null;

  /** Current App + Server version, stamped as fixedInVersion when moving to Released. */
  private currentVersion = this.versionService.appDisplay;

  ngOnInit(): void {
    this.versionService.getCombinedVersion().subscribe(v => (this.currentVersion = v));

    const id = this.route.snapshot.paramMap.get('id');
    if (id) this.loadIssue(id);
  }

  loadIssue(id: string): void {
    this.loading = true;
    this.issueService.getById(id).subscribe({
      next: i => { this.issue = i; this.loading = false; },
      error: () => { this.loading = false; this.toast.error('Error loading issue'); }
    });
  }

  get canEdit(): boolean { return this.currentUserState.canEdit('Issues', 'Issues List'); }
  get canCreate(): boolean { return this.currentUserState.canCreate('Issues', 'Issues List'); }
  get canDelete(): boolean { return this.currentUserState.canDelete('Issues', 'Issues List'); }
  get isReporter(): boolean { return !!this.issue && this.issue.reportedByUserId === this.currentUserState.user()?.id; }
  get canReporterAct(): boolean { return this.isReporter && this.issue?.status === 'Open'; }
  get myName(): string { return this.currentUserState.user()?.fullName ?? '?'; }

  /** Keycloak `developer` realm role — gates the developer-notes button. Mirrored server-side by [DeveloperOnly]. */
  get isDeveloper(): boolean { return this.currentUserState.isDeveloper(); }

  /** A comment may only be edited or deleted by its author — enforced server-side too. */
  isMyComment(c: IssueComment): boolean { return c.authorUserId === this.currentUserState.user()?.id; }

  statusLabel(s?: string | null): string { return s ? (this.statusLabels[s] ?? s) : '—'; }

  private static readonly STATUS_COLORS: Record<string, { bg: string; fg: string }> = {
    Open: { bg: '#E1F5FE', fg: '#0277BD' },
    Reviewed: { bg: '#D1C4E9', fg: '#4527A0' },
    InProgress: { bg: '#BBDEFB', fg: '#0D47A1' },
    Done: { bg: '#C8E6C9', fg: '#2E7D32' },
    Staged: { bg: '#B2DFDB', fg: '#00695C' },
    Released: { bg: '#DCEDC8', fg: '#558B2F' },
    Closed: { bg: '#CFD8DC', fg: '#37474F' },
    Parked: { bg: '#D7CCC8', fg: '#4E342E' },
    Rejected: { bg: '#FFCDD2', fg: '#B71C1C' }
  };

  // Low → light blue, Medium → yellow-orange, High → orange-red.
  private static readonly PRIORITY_COLORS: Record<string, { bg: string; fg: string }> = {
    Low: { bg: '#B3E5FC', fg: '#01579B' },
    Medium: { bg: '#FFE082', fg: '#E65100' },
    High: { bg: '#FFB299', fg: '#BF360C' },
    Critical: { bg: '#FF3D00', fg: '#FFFFFF' }
  };

  statusStyle(s?: string | null): Record<string, string> {
    const c = s ? IssueDetailComponent.STATUS_COLORS[s] : null;
    return c ? { 'background-color': c.bg, color: c.fg } : {};
  }

  /** Thick coloured left border on each transition option in the status dropdown. */
  statusBorderStyle(s?: string | null): Record<string, string> {
    const c = s ? IssueDetailComponent.STATUS_COLORS[s] : null;
    return c ? { 'border-left': '4px solid ' + c.fg } : {};
  }

  priorityStyle(p?: string | null): Record<string, string> {
    const c = p ? IssueDetailComponent.PRIORITY_COLORS[p] : null;
    return c ? { 'background-color': c.bg, color: c.fg } : {};
  }

  // --- Edit title/description ---
  startEdit(): void {
    if (!this.issue) return;
    this.editTitle = this.issue.title;
    this.editDescription = this.issue.description;
    this.editType = this.issue.type;
    this.editPriority = this.issue.priority;
    this.editing = true;
  }

  cancelEdit(): void { this.editing = false; }

  saveEdit(): void {
    if (!this.issue || !this.editTitle.trim() || !this.editDescription.trim()) return;
    this.issueService.update(this.issue.id, {
      title: this.editTitle.trim(),
      description: this.editDescription,
      type: this.editType,
      priority: this.editPriority,
      categoryId: this.issue.categoryId,
      subCategoryId: this.issue.subCategoryId ?? null
    }).subscribe({
      next: i => { this.issue = i; this.editing = false; this.toast.success('Issue updated'); },
      error: () => this.toast.error('Could not update issue')
    });
  }

  // --- Status (Jira-style, no note) ---
  selectStatus(status: string): void {
    if (!this.issue) return;
    const request: IssueStatusChangeRequest = { status };
    if (status === 'Released') request.fixedInVersion = this.currentVersion;
    this.issueService.changeStatus(this.issue.id, request).subscribe({
      next: i => { this.issue = i; this.toast.success('Status updated'); },
      error: () => this.toast.error('Could not change status')
    });
  }

  // --- Verification (any user who can see the issue; the server derives the environment) ---
  verifiedWhere(environment?: string | null): string {
    if (environment === 'Staging') return 'on staging';
    if (environment === 'Production') return 'in production';
    return '';
  }

  verify(): void {
    if (!this.issue) return;
    this.issueService.verify(this.issue.id).subscribe({
      next: i => { this.issue = i; this.toast.success('Thanks — the fix is marked verified'); },
      error: () => this.toast.error('Could not verify the fix')
    });
  }

  /**
   * Send the fix back to the dev team. What is wrong is not captured here: it routinely runs to
   * thousands of characters, which belongs in the comment thread — readable, and open to a reply —
   * rather than squeezed into the one-line-per-transition history. So we confirm, send it back, and
   * drop the tester straight into the comment box.
   */
  sendVerificationFailed(): void {
    if (!this.issue) return;
    this.confirmationDialog
      .openConfirmationDialog(
        'Send the fix back?',
        'The issue returns to In Progress. Please add a comment explaining what is still wrong.',
        'Send back', 'Cancel', 'undo', 'clear'
      )
      .subscribe(confirmed => {
        if (!confirmed) return;
        this.issueService.verificationFailed(this.issue!.id).subscribe({
          next: i => {
            this.issue = i;
            this.toast.success('Sent back to the dev team — please add a comment');
            this.focusCommentBox();
          },
          error: () => this.toast.error('Could not send it back')
        });
      });
  }

  /** Scrolls the comment box into view and focuses it, so the explanation is the obvious next step. */
  private focusCommentBox(): void {
    setTimeout(() => {
      const box = this.commentBox?.nativeElement;
      if (!box) return;
      box.scrollIntoView({ behavior: 'smooth', block: 'center' });
      box.focus();
    });
  }

  // --- Comments ---
  addComment(): void {
    if (!this.issue || !this.newComment.trim()) return;
    this.issueService.addComment(this.issue.id, { body: this.newComment.trim() }).subscribe({
      next: c => { this.issue!.comments = [...this.issue!.comments, c]; this.newComment = ''; },
      error: () => this.toast.error('Could not add comment')
    });
  }

  startCommentEdit(c: IssueComment): void {
    this.editingCommentId = c.id;
    this.editCommentBody = c.body;
  }

  cancelCommentEdit(): void {
    this.editingCommentId = null;
    this.editCommentBody = '';
  }

  saveCommentEdit(c: IssueComment): void {
    if (!this.issue || !this.editCommentBody.trim()) return;
    this.issueService.updateComment(this.issue.id, c.id, { body: this.editCommentBody.trim() }).subscribe({
      next: updated => {
        this.issue!.comments = this.issue!.comments.map(x => (x.id === updated.id ? updated : x));
        this.cancelCommentEdit();
        this.toast.success('Comment updated');
      },
      error: () => this.toast.error('Could not update comment')
    });
  }

  deleteComment(c: IssueComment): void {
    if (!this.issue) return;
    this.confirmationDialog
      .openDeleteConfirmationDialog('', 'Delete this comment? This action cannot be undone.')
      .subscribe(confirmed => {
        if (!confirmed) return;
        this.issueService.deleteComment(this.issue!.id, c.id).subscribe({
          next: () => {
            this.issue!.comments = this.issue!.comments.filter(x => x.id !== c.id);
            if (this.editingCommentId === c.id) this.cancelCommentEdit();
            this.toast.success('Comment deleted');
          },
          error: () => this.toast.error('Could not delete comment')
        });
      });
  }

  // --- Developer notes (developer role only) ---
  openDeveloperNotes(): void {
    if (!this.issue) return;
    const data: DeveloperNotesDialogData = { issueId: this.issue.id, issueNumber: this.issue.number };
    this.dialog
      .open(DeveloperNotesDialogComponent, { width: '640px', data })
      .afterClosed()
      .subscribe(saved => {
        // Only a save closes with a payload; keep the button's dot in step with it.
        if (saved) this.issue!.hasDeveloperNotes = !!saved.notes?.trim();
      });
  }

  // --- Attachments ---
  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    this.selectedFile = input.files?.[0] ?? null;
  }

  uploadAttachment(): void {
    if (!this.issue || !this.selectedFile) return;
    this.issueService.addAttachment(this.issue.id, this.selectedFile).subscribe({
      next: a => { this.issue!.attachments = [...this.issue!.attachments, a]; this.selectedFile = null; this.toast.success('Attachment uploaded'); },
      error: () => this.toast.error('Upload failed')
    });
  }

  download(att: IssueAttachment): void {
    this.issueService.downloadAttachment(att.id).subscribe({
      next: blob => {
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = att.fileName;
        link.click();
        URL.revokeObjectURL(url);
      },
      error: () => this.toast.error('Download failed')
    });
  }

  // --- Reporter / dev actions ---
  withdraw(): void {
    if (!this.issue) return;
    this.confirmationDialog
      .openConfirmationDialog(
        'Withdraw Issue',
        'Withdraw this issue? It will be marked Rejected.',
        'Withdraw', 'Cancel', 'undo', 'clear'
      )
      .subscribe(confirmed => {
        if (!confirmed) return;
        this.issueService.withdraw(this.issue!.id).subscribe({
          next: i => { this.issue = i; this.toast.success('Issue withdrawn'); },
          error: () => this.toast.error('Could not withdraw')
        });
      });
  }

  deleteOwn(): void {
    this.confirmDeleteIssue(() => this.issueService.deleteOwn(this.issue!.id));
  }

  deleteIssue(): void {
    this.confirmDeleteIssue(() => this.issueService.delete(this.issue!.id));
  }

  /** Shared confirm + delete + navigate-away, for both the reporter's and the developer's delete. */
  private confirmDeleteIssue(deletion: () => Observable<void>): void {
    if (!this.issue) return;
    this.confirmationDialog
      .openDeleteConfirmationDialog('', `Delete issue #${this.issue.number} permanently? This action cannot be undone.`)
      .subscribe(confirmed => {
        if (!confirmed) return;
        deletion().subscribe({
          next: () => { this.toast.success('Issue deleted'); this.router.navigate(['/issues']); },
          error: () => this.toast.error('Could not delete')
        });
      });
  }

  back(): void { this.router.navigate(['/issues']); }
}
